import type { Logger, LogContext, LogLevel } from "@fluentry/domain";
import { parseEnv } from "../config/env";

type ConsoleLike = Pick<Console, "debug" | "info" | "warn" | "error">;

type LoggerOptions = {
  level?: LogLevel;
  context?: LogContext;
  env?: Record<string, any>;
  console?: ConsoleLike;
};

const levelPriority: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function resolveLevel(options: LoggerOptions): LogLevel {
  if (options.level) return options.level;
  const env = options.env ?? {};
  const parsed = parseEnv(env);
  return parsed.LOG_LEVEL || "info";
}

type SerializedError = {
  name: string;
  message: string;
  stack?: string;
  cause?: SerializedError | unknown;
};

function serializeError(value: Error): SerializedError {
  return {
    name: value.name,
    message: value.message,
    stack: value.stack,
    cause: value.cause instanceof Error ? serializeError(value.cause) : value.cause,
  };
}

function normalizeContext(context?: LogContext): LogContext {
  if (!context) return {};
  const normalized: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) continue;
    normalized[key] = value instanceof Error ? serializeError(value) : value;
  }
  return normalized;
}

export function createLogger(options: LoggerOptions = {}): Logger {
  const level = resolveLevel(options);
  const baseContext = normalizeContext(options.context);
  const consoleLike = options.console ?? console;

  const shouldLog = (messageLevel: LogLevel) => levelPriority[messageLevel] >= levelPriority[level];

  const log = (messageLevel: LogLevel, message: string, context?: LogContext) => {
    if (!shouldLog(messageLevel)) return;
    const normalizedContext = normalizeContext(context);
    const payload = {
      level: messageLevel,
      msg: message,
      time: new Date().toISOString(),
      ...baseContext,
      ...normalizedContext,
    };

    const writer = consoleLike[messageLevel] ?? consoleLike.info;
    writer(JSON.stringify(payload));
  };

  return {
    child(childContext: LogContext) {
      const nextContext = {
        ...baseContext,
        ...normalizeContext(childContext),
      };
      return createLogger({
        level,
        context: nextContext,
        console: consoleLike,
        env: options.env,
      });
    },
    debug(message: string, context?: LogContext) {
      log("debug", message, context);
    },
    info(message: string, context?: LogContext) {
      log("info", message, context);
    },
    warn(message: string, context?: LogContext) {
      log("warn", message, context);
    },
    error(message: string, context?: LogContext) {
      log("error", message, context);
    },
  };
}
